// @ts-strict

import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DatePickerService } from '@app/core/services/date-picker/date-picker.service';
import { NgbCalendar, NgbDate, NgbDateNativeAdapter, NgbDatepicker, NgbDatepickerI18n, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { isEqual } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { IDateRange, StatisticsDatePickers } from '../../statistics-top-menu-new.component';
import { AvailableDatePeriod } from '../date-picker-sidebar/statistics-date-picker-sidebar.component';
import { CustomDatepickerI18n, I18n, StatisticsDatePickerService } from './custom-date-picker.service';

const INITIAL_DATE_PERIOD = 'last_7_days';

@Component({
  selector: 'app-statistics-date-picker',
  templateUrl: './statistics-date-picker.component.html',
  styleUrls: ['./statistics-date-picker.component.scss'],
  providers: [
    I18n,
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n },
    { provide: NgbDateNativeAdapter, useClass: NgbDateNativeAdapter },
    CustomDatepickerI18n,
    StatisticsDatePickerService
  ]
})
export class StatisitcsDatePickerComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('dp', { static: true }) datePicker!: NgbDatepicker;

  @Input() currentDatePicker: StatisticsDatePickers = 'default';
  @Input() selectedPeriod: AvailableDatePeriod = INITIAL_DATE_PERIOD;
  @Input() dateRanges: IDateRange[] = [];

  @Output() emitSelectedPeriod = new EventEmitter<{ dateFrom: NgbDate; dateTo: NgbDate | null }>();
  @Output() setSelectedPeriod = new EventEmitter<AvailableDatePeriod>();

  public isDateFromValid = true;
  public isDateToValid = true;

  public rangeErrorFrom = false;
  public rangeErrorTo = false;

  public hoveredDate: NgbDate | null = null;
  public dateClicked = false;

  public fromDateInput: string = '';
  public toDateInput: string = '';

  public fromDate: NgbDate = this.calendar.getPrev(this.calendar.getToday(), 'd', 6);
  public toDate: NgbDate | null = this.calendar.getToday();

  public firstDate?: NgbDate;
  public lastDate?: NgbDate;

  public maxDate: NgbDate = this.calendar.getToday();
  public minDate: NgbDate = new NgbDate(2014, 1, 1);

  public errorRanges = {
    minDate: this.minDate,
    maxDate: this.maxDate
  };

  private destroy$ = new Subject<void>();

  constructor(
    private readonly calendar: NgbCalendar,
    private readonly datePickerService: DatePickerService,
    private readonly statisticsDatePickerService: StatisticsDatePickerService
  ) {}

  get isComparePicker(): boolean {
    return this.currentDatePicker === 'compare';
  }

  get numberOfMonths(): number {
    return window.innerWidth > 768 ? 2 : 1;
  }

  ngOnInit(): void {
    this.setFirstAndLastDate();

    this.datePicker.navigate
      .pipe(
        tap(() => {
          this.setFirstAndLastDate();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!isEqual(changes.selectedPeriod?.currentValue, changes.selectedPeriod?.previousValue)) {
      this.handleSelectedPeriodChange();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDateSelection(date: NgbDate): void {
    this.setSelectedPeriod.emit('user');

    const isSelectingAfterOrEqualsCurrent = this.fromDate && !this.toDate && (date.after(this.fromDate) || date.equals(this.fromDate));

    if (isSelectingAfterOrEqualsCurrent) {
      this.toDate = date;
      this.onDateToInput(this.datePickerService.formatDate(date));

      this.handleSelectedPeriod();

      return;
    }

    this.toDate = null;
    this.toDateInput = '';

    this.fromDate = date;
    this.onDateFromInput(this.datePickerService.formatDate(this.fromDate));

    this.handleSelectedPeriod();
  }

  public handleHoverOverDate = (date: NgbDate | null): void => {
    this.hoveredDate = date;

    if (!date) {
      this.fromDateInput = this.datePickerService.formatDate(this.fromDate);
      this.toDateInput = this.toDate ? this.datePickerService.formatDate(this.toDate) : '';
    }

    const isHoveringAfterOrEqualsCurrent = this.fromDate && !this.toDate && date && (date.after(this.fromDate) || date.equals(this.fromDate));

    if (isHoveringAfterOrEqualsCurrent) {
      this.toDateInput = this.datePickerService.formatDate(date);

      return;
    }

    if (date) this.fromDateInput = this.datePickerService.formatDate(date);
  };

  public isHovered(date: NgbDate): boolean {
    const isHovered = this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);

    return isHovered ?? false;
  }

  public isInside(date: NgbDate): boolean {
    const isInside = this.toDate && date.after(this.fromDate) && date.before(this.toDate);

    return isInside ?? false;
  }

  public isRange(date: NgbDate): boolean {
    return this.isInside(date) || this.isHovered(date);
  }

  public isRangeStart(date: NgbDate): boolean {
    return date.equals(this.fromDate) && !(this.toDate && date.equals(this.toDate));
  }

  public isRangeEnd(date: NgbDate): boolean {
    const isRangeEnd = this.toDate && date.equals(this.toDate) && !date.equals(this.fromDate);

    return isRangeEnd ?? false;
  }

  public isRangeOneDay(date: NgbDate): boolean {
    const isRangeOneDay = this.fromDate && this.toDate && date.equals(this.fromDate) && date.equals(this.toDate);

    return isRangeOneDay ?? false;
  }

  public isInsideMonth(date: NgbDate): boolean {
    return date.before(this.firstDate) || date.after(this.lastDate);
  }

  public onDateToInput = (value: string, tooltip?: NgbTooltip) => {
    this.toDateInput = value;
    this.setSelectedPeriod.emit('user');

    this.isDateToValid = this.statisticsDatePickerService.checkIfDateStringIsValid(value);
    this.rangeErrorTo = this.statisticsDatePickerService.checkIfDateRangeExceeded(value, { maxDate: this.maxDate, minDate: this.minDate });

    this.rangeErrorTo ? tooltip?.open() : tooltip?.close();

    if (this.isDateToValid && !this.rangeErrorTo) {
      this.toDate = this.statisticsDatePickerService.createNgbDateDateFromString(value);
      this.datePicker.navigateTo(this.toDate);

      this.handleSelectedPeriod();
    }
  };

  public onDateFromInput(value: string, tooltip?: NgbTooltip): void {
    this.fromDateInput = value;
    this.setSelectedPeriod.emit('user');

    this.isDateFromValid = this.statisticsDatePickerService.checkIfDateStringIsValid(value);
    this.rangeErrorFrom = this.statisticsDatePickerService.checkIfDateRangeExceeded(value, { maxDate: this.maxDate, minDate: this.minDate });

    this.rangeErrorFrom ? tooltip?.open() : tooltip?.close();

    if (this.isDateFromValid && !this.rangeErrorFrom) {
      this.fromDate = this.statisticsDatePickerService.createNgbDateDateFromString(value);
      this.datePicker.navigateTo(this.fromDate);

      this.handleSelectedPeriod();
    }
  }

  public onBlur = (inputType: 'from' | 'to'): void => {
    if (inputType === 'from' && !this.fromDateInput && !this.isDateFromValid && this.fromDate) {
      this.fromDateInput = this.datePickerService.formatDate(this.fromDate);
    }

    if (inputType === 'to' && !this.toDateInput && !this.isDateToValid && this.toDate) {
      this.toDateInput = this.datePickerService.formatDate(this.toDate);
    }
  };

  private handleSelectedPeriodChange = () => {
    const newPeriod = this.dateRanges.find(({ range }) => range === this.selectedPeriod);

    if (newPeriod) {
      const { dateFrom, dateTo } = newPeriod;
      this.fromDate = dateFrom;
      this.toDate = dateTo;
      this.datePicker.navigateTo(dateTo);
      this.fromDateInput = this.datePickerService.formatDate(this.fromDate);
      this.toDateInput = this.datePickerService.formatDate(this.toDate);

      this.handleSelectedPeriod();

      return;
    }
  };

  private setFirstAndLastDate = () => {
    this.firstDate = this.datePicker.state.firstDate;
    this.lastDate = this.datePicker.state.lastDate;
  };

  private handleSelectedPeriod = () => {
    if (this.fromDate) this.emitSelectedPeriod.emit({ dateFrom: this.fromDate, dateTo: this.toDate });
  };
}
